import {Sheet, SheetContent, SheetTrigger} from '@components/ui/sheet'
import {useTranslation} from '@/translations/i18n.tsx'
import {FC, ReactElement, useEffect, useMemo, useRef, useState} from 'react'
import {Spinner} from '@components/ui/spinner'
import {Button} from '@components/ui/button'
import {InputText} from '@components/commons/input-text'
import {ChevronDownIcon, PlusIcon, SearchLgIcon} from '@components/ui/icon'
import {AreaItem} from '@features/settings/components/area-item'
import {CountriesSelect} from '@features/settings/components/countries-select'
import {useMe} from '@features/user/queries/useMe'
import {cn, raise} from '@utilities/helpers'
import {useGeos} from '@features/settings/queries/useGeos'
import {useUpdateFreelancerAreas} from '@features/settings/queries/useUpdateFreelancerAreas'
import {SelectCountriesMobileModal} from '@features/settings/components/select-countries-mobile-modal'
import {Area} from '@features/settings/types'
import {ModalFooter, ModalHeaderTitle} from '@components/ui/modal-atoms'
import {Dialog, DialogContent, DialogTrigger} from '@components/ui/dialog'
import {useMediaQuery} from '@hooks/use-media-query'

interface AddAreasModalProps {
    children: ReactElement
}

export const AddAreasModal: FC<AddAreasModalProps> = ({children}) => {
    const isLarge = useMediaQuery({breakpoint: 'md'})
    const [open, setOpen] = useState(false)

    const contentProps = {
        onClose: () => setOpen(false),
        isMobile: !isLarge
    }
    if (isLarge) {
        return (
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger render={children} nativeButton={false} />
                <DialogContent className="w-127.5 h-167.5">
                    <ModalContent {...contentProps} />
                </DialogContent>
            </Dialog>
        )
    }
    return (
        <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger render={children} nativeButton={false} />
            <SheetContent className="h-[calc(100%-(--spacing(5)))]">
                <ModalContent {...contentProps} />
            </SheetContent>
        </Sheet>
    )
}

export type SelectedCountry = {
    label: string
    country_code: string
}
interface ModalContentProps {
    onClose: () => void
    isMobile: boolean
}

export const ModalContent: FC<ModalContentProps> = ({onClose, isMobile}) => {
    const {t} = useTranslation()
    const userMeQuery = useMe({throwOnError: true})
    const user = userMeQuery.data ?? raise('user is nullish')
    const userMainArea = user.photographerGeos.find(area => area.isMain)
    const region = userMainArea?.geo.region ?? user.region
    const country = {
        label: userMainArea?.geo.extendedCountry ?? user.country ?? '',
        country_code: userMainArea?.geo.country ?? user.country ?? ''
    }
    const areasData = user.photographerGeos.map(area => {
        return {id: area.geoId, label: area.geo.geo, country: area.geo.country, is_main: area.isMain}
    })
    const searchWrapperRef = useRef<HTMLDivElement>(null)
    const scrollAreaRef = useRef<HTMLDivElement>(null)
    const [stickyBottomPosition, setStickyBottomPosition] = useState(false)

    const [selectedCountry, setSelectedCountry] = useState<SelectedCountry>(country)
    const [searchAreas, setSearchAreas] = useState('')
    const [selectedAreas, setSelectedAreas] = useState<Area[]>([])
    const areasQuery = useGeos({countries: [selectedCountry.country_code]})
    const updateGeosMutation = useUpdateFreelancerAreas({
        onSuccess: onClose
    })
    const countries = [...new Set(selectedAreas.map(area => area.country))]

    const handleSave = () => {
        updateGeosMutation.mutate({
            urlParams: {userId: user.id},
            payload: {geo_ids: [...areasData, ...selectedAreas].map(area => area.id)}
        })
    }

    const filteredAreas = useMemo(() => {
        if (areasQuery.data) {
            return searchAreas
                ? areasQuery.data.geos.filter(area => area.geo.toLowerCase().includes(searchAreas.toLowerCase()))
                : areasQuery.data.geos
        }
        return []
    }, [areasQuery.data, searchAreas])

    const removeSelectedArea = (areaId: number) => {
        setSelectedAreas(prev => prev.filter(area => area.id !== areaId))
    }

    const onChangeCountry = (value: SelectedCountry) => setSelectedCountry(value)

    useEffect(() => {
        const handleScroll = () => {
            const bodyWrapper = scrollAreaRef.current
            const searchWrapper = searchWrapperRef.current

            if (!bodyWrapper || !searchWrapper) return
            const bodyRect = bodyWrapper.getBoundingClientRect()
            const searchRect = searchWrapper.getBoundingClientRect()

            if (Math.floor(bodyRect.bottom) <= Math.floor(searchRect.bottom)) {
                setStickyBottomPosition(true)
            }
        }

        if (scrollAreaRef?.current) {
            scrollAreaRef.current.addEventListener('scroll', handleScroll)
        }

        return () => {
            if (scrollAreaRef?.current) {
                scrollAreaRef.current.removeEventListener('scroll', handleScroll)
            }
        }
    }, [scrollAreaRef?.current])

    return (
        <div className="grid grid-rows-[auto_minmax(0,1fr)_auto] h-full overflow-auto">
            <ModalHeaderTitle> {t('settings:areas:areas_label')} </ModalHeaderTitle>

            <div className="grid grid-rows-[auto_auto_auto_1fr] px-6 relative h-full overflow-auto" ref={scrollAreaRef}>
                {!!selectedAreas.length && (
                    <div className="flex flex-wrap gap-3 pt-6">
                        {selectedAreas.map(area => (
                            <AreaItem
                                key={area.id}
                                label={area.geo}
                                countryCode={countries.length > 1 ? area.country : undefined}
                                removeSelectedArea={() => removeSelectedArea(area.id)}
                            />
                        ))}
                    </div>
                )}

                <div
                    ref={searchWrapperRef}
                    className={cn(
                        'flex flex-col gap-4 sticky top-0 z-10 py-4 bg-white transition-[top,bottom] duration-200',
                        stickyBottomPosition && 'bottom-0'
                    )}
                >
                    <InputText
                        type="text"
                        startSlot={<SearchLgIcon size={20} fill="var(--color-neutral-500)" />}
                        onChange={event => setSearchAreas(event.target.value)}
                        placeholder={t('settings:areas:search_area_placeholder')}
                    />
                    <div className="flex items-center gap-1 text-sm text-neutral-600 [&_span]:flex [&_span]:cursor-pointer [&_span]:items-center [&_span]:text-neutral-900 [&_span_svg]:fill-neutral-700">
                        <p>{t('settings:areas:showing_areas')} </p>{' '}
                        {isMobile ? (
                            <SelectCountriesMobileModal
                                region={region}
                                selectedCountry={selectedCountry}
                                onChangeCountry={onChangeCountry}
                            >
                                <button type="button" className="flex items-center text-neutral-900">
                                    {selectedCountry.label}
                                    <ChevronDownIcon size={20} />
                                </button>
                            </SelectCountriesMobileModal>
                        ) : (
                            <CountriesSelect
                                region={region}
                                onChangeCountry={onChangeCountry}
                                selectedCountry={selectedCountry}
                                isMobile={false}
                                trigger={
                                    <span>
                                        {selectedCountry.label}
                                        <ChevronDownIcon size={20} />
                                    </span>
                                }
                            />
                        )}
                    </div>
                </div>

                <div className="grid grid-rows-[auto_1fr] gap-2 w-full pb-6">
                    {areasQuery.isLoading ? (
                        <div className="flex w-full justify-center">
                            <Spinner size={40} />
                        </div>
                    ) : (
                        <>
                            {filteredAreas.map(area => {
                                const isSelected = !![...areasData, ...selectedAreas].find(item => item.id == area.id)
                                return (
                                    !isSelected && (
                                        <div
                                            key={area.id}
                                            className="flex w-full justify-between border-b border-neutral-300 p-2.5 text-md leading-normal [&_svg]:cursor-pointer [&_svg]:fill-neutral-600"
                                        >
                                            {area.geo}
                                            <PlusIcon
                                                size={20}
                                                onClick={() => setSelectedAreas(prev => [...prev, area])}
                                            />
                                        </div>
                                    )
                                )
                            })}
                        </>
                    )}
                </div>
            </div>

            <ModalFooter>
                <Button variant="tertiary" onClick={onClose} disabled={updateGeosMutation.isPending}>
                    {t('commons:cancel')}
                </Button>
                <Button onClick={handleSave} disabled={updateGeosMutation.isPending}>
                    {t('commons:save')}
                    {updateGeosMutation.isPending && <Spinner />}
                </Button>
            </ModalFooter>
        </div>
    )
}
